import { type FC, useState } from "react";
import { useNavigate } from "react-router";
import { Box, ButtonBase, Stack, Typography, IconButton } from "@mui/material";
import { flattenTenantIds, type OrganisationHierarchy } from "../../../api/organisations.types";
import { TenantIcon2, ChevronDownIcon, ChevronRightIcon } from "../../../shared/icons";
import { useParsedParams } from "../../../shared/useParsedParams";
import { useTranslation } from "@privacy/translations";
import { z } from "zod";

type OrganisationTreeProps = { organisationHierarchy: OrganisationHierarchy };

export const OrganisationTree: FC<OrganisationTreeProps> = ({ organisationHierarchy }) => {
  const { t } = useTranslation();
  const parsedParams = useParsedParams(z.object({ tenantId: z.string() }));
  const navigate = useNavigate();
  const [expandedIds, setExpandedIds] = useState(() => new Set(flattenTenantIds(organisationHierarchy)));

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelect = (tenantId: string) =>
    navigate({ pathname: `../${tenantId}` }, { replace: true, relative: "path" });

  const renderTree = (node: OrganisationHierarchy, depth = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const isSelected = parsedParams.tenantId === node.id;

    return (
      <Box key={node.id}>
        <Stack
          direction="row"
          sx={[
            {
              alignItems: "center",
            },
            theme => ({
              "mx": 1,
              "mb": 0.75,
              "py": 0.5,
              "pl": 1 + 1.5 * depth,
              "pr": 1,
              "borderRadius": 1,
              "bgcolor": isSelected ? theme.palette.action.selected : "transparent",
              "&:hover": { bgcolor: theme.palette.action.hover },
            }),
          ]}
        >
          <IconButton
            aria-label={t(isExpanded ? "Common.Collapse" : "Common.Expand")}
            sx={{ visibility: hasChildren ? "visible" : "hidden" }}
            onClick={() => toggleExpand(node.id)}
          >
            {isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
          </IconButton>
          <ButtonBase
            onClick={() => handleSelect(node.id)}
            aria-selected={isSelected}
            sx={theme => ({
              "flex": 1,
              "borderRadius": 1,
              "justifyContent": "flex-start",
              "&:focus-visible": { outline: "2px solid", outlineColor: theme.palette.primary["300"] },
            })}
          >
            <Box sx={{ ml: 1.5, display: "inline-flex" }}>
              <TenantIcon2 size="sm" />
            </Box>
            <Typography
              sx={{ ml: 2, fontSize: theme => theme.typography.pxToRem(14), lineHeight: "1.1", fontWeight: "600" }}
            >
              {node.organizationName}
            </Typography>
          </ButtonBase>
        </Stack>
        {isExpanded && hasChildren && node.children.map(child => renderTree(child, depth + 1))}
      </Box>
    );
  };

  return <Stack>{renderTree(organisationHierarchy)}</Stack>;
};
